import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadForm from "@/components/LeadForm";
import { breadcrumbSchema, articleSchema } from "@/lib/schema";
import { CalendarBlank, Clock } from "@phosphor-icons/react";

// Editorial photo pool — used to intersperse full-bleed images between
// major sections of the AI-generated markdown body so long-reads don't feel
// like a wall of text. Every image is a stock Unsplash Dubai / UAE shot.
const SECTION_IMAGES = [
  "https://images.unsplash.com/photo-1546412414-8035e1776c9a?w=1600&q=85",
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600&q=85",
  "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1600&q=85",
  "https://images.unsplash.com/photo-1494783367193-149034c05e8f?w=1600&q=85",
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=85",
];

function estimateReadTime(text) {
  const words = String(text || "").trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

// Strip the AI's leading H1 (which duplicates the page title) and split the
// remaining markdown by H2 (## ) so we can slot in a photo between sections.
function splitByH2(body) {
  if (!body) return [];
  let md = body.replace(/^#\s+.+\n+/, "");
  const parts = md.split(/\n(?=##\s)/);
  return parts.map((p) => p.trim()).filter(Boolean);
}

const proseComponents = {
  h1: () => null,   // suppress any stray H1 — the hero already has the title
  h2: ({ children }) => (
    <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-[#050d19] mt-14 mb-5">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-serif text-xl sm:text-2xl leading-tight text-[#001588] mt-10 mb-3">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-neutral-700 leading-[1.85] text-lg mb-5">{children}</p>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-6 space-y-3 text-neutral-700 text-lg leading-[1.75] my-6 marker:text-[#ffffff] marker:font-serif">{children}</ol>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-6 space-y-2 text-neutral-700 text-lg leading-[1.75] my-6 marker:text-[#001588]">{children}</ul>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[#ffffff] pl-6 my-8 italic text-neutral-700 text-xl leading-relaxed font-serif">{children}</blockquote>
  ),
  strong: ({ children }) => <strong className="text-[#050d19] font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic text-neutral-700">{children}</em>,
  a: ({ children, href }) => (
    <a href={href} className="text-[#001588] underline underline-offset-4 decoration-[#ffffff] hover:decoration-[#001588]" target={String(href || "").startsWith("http") ? "_blank" : undefined} rel="noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-10 border-neutral-200" />,
};

export default function JournalDetail() {
  const { slug } = useParams();
  const [j, setJ] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setJ(null); setNotFound(false);
    api.get(`/journal/${slug}`)
      .then((r) => setJ(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); });
    api.get("/journal").then((r) => setRelated((r.data.items || []).filter((x) => x.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-serif text-3xl mb-3">Article not found</h1>
        <Link to="/journal" className="text-sm text-[#001588] underline">Back to Journal</Link>
      </div>
    );
  }
  if (!j) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Journal", path: "/journal" },
    { name: j.title, path: `/journal/${slug}` },
  ];
  const sections = splitByH2(j.body);
  const readMins = estimateReadTime(j.body);
  const publishedDate = (j.created_at || "").slice(0, 10);

  return (
    <article data-testid={`journal-detail-${slug}`}>
      <SEO
        title={j.seo_title || j.title}
        description={j.seo_description || j.excerpt}
        path={`/journal/${slug}`}
        image={j.hero_image}
        type="article"
        schema={[breadcrumbSchema(crumbs), articleSchema(j)]}
      />

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end overflow-hidden bg-[#001588]">
        <img src={j.hero_image} alt={j.title} className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent pointer-events-none"></div>
        <div className="relative max-w-[1000px] mx-auto px-6 pb-20 w-full text-white" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}>
          <Breadcrumbs items={crumbs} />
          <div className="overline text-[#ffffff] mb-5">{j.category || "Market intelligence"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl">{j.title}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/70">
            <span>By <span className="text-white">{j.author || "Point Penta research desk"}</span></span>
            {publishedDate && <span className="flex items-center gap-1"><CalendarBlank size={14} weight="thin" /> {publishedDate}</span>}
            <span className="flex items-center gap-1"><Clock size={14} weight="thin" /> {readMins} min read</span>
          </div>
        </div>
      </section>

      {/* Article body */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-[760px] mx-auto px-6">
          {j.excerpt && (
            <p className="font-serif text-2xl sm:text-3xl leading-[1.35] text-neutral-800 mb-14 border-l-2 border-[#ffffff] pl-5">
              {j.excerpt}
            </p>
          )}

          {sections.map((section, idx) => (
            <div key={idx}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={proseComponents}>{section}</ReactMarkdown>
              {/* Slot a photo after the 1st and 3rd H2 sections */}
              {(idx === 0 || idx === 2) && SECTION_IMAGES[idx % SECTION_IMAGES.length] && (
                <figure className="my-14 -mx-6 sm:mx-0">
                  <img
                    src={SECTION_IMAGES[(idx + 1) % SECTION_IMAGES.length]}
                    alt={`Editorial imagery for ${j.title} — Dubai property market`}
                    loading="lazy"
                    className="w-full aspect-[16/9] object-cover"
                  />
                  <figcaption className="text-xs text-neutral-500 mt-3 text-center italic">
                    Editorial imagery · Dubai skyline
                  </figcaption>
                </figure>
              )}
            </div>
          ))}

          <hr className="my-14 border-neutral-200" />

          {/* Author + CTA */}
          <div className="bg-[#ffffff] p-8 border border-[#e5e7eb]">
            <div className="overline text-[#001588] mb-3">About the desk</div>
            <p className="text-neutral-700 leading-relaxed mb-6">
              Point Penta&rsquo;s research desk publishes editorial market analysis every week. If you&rsquo;re
              looking at property in {j.category === "Community deep-dive" ? "this community" : "Dubai"},
              an advisor will share the full sales pack — inventory, comparables and the current pricing
              band — within one business day.
            </p>
            <LeadForm
              source={`journal-${slug}`}
              interest="research"
              testIdPrefix={`journal-lead-${slug}`}
              submitLabel="Request the full sales pack"
            />
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 border-t border-neutral-200 bg-[#ffffff]">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="overline text-[#001588] mb-3">Keep reading</div>
            <h2 className="font-serif text-3xl lg:text-4xl mb-10">More from the desk</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/journal/${r.slug}`} className="group block bg-white border border-neutral-200 hover:border-[#001588] transition-colors">
                  {r.hero_image && <img src={r.hero_image} alt={r.title} className="aspect-[4/3] object-cover w-full" loading="lazy" />}
                  <div className="p-5">
                    <div className="overline text-[#001588] mb-2">{r.category || "Journal"}</div>
                    <h3 className="font-serif text-lg leading-tight group-hover:text-[#001588]">{r.title}</h3>
                    <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{r.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
